import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import MainLayout from '../layouts/MainLayout';
import { 
  MapPin, 
  Phone, 
  Globe, 
  Clock, 
  Building, 
  Save,
  Edit,
  Facebook,
  Instagram,
  Linkedin
} from 'lucide-react';

interface CompanyInfo {
  id?: string;
  companyName: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  contact: {
    phone: string;
    fax?: string;
    email: string;
    website?: string;
  };
  businessHours: {
    weekdays: string;
    saturday?: string;
    sunday?: string;
  };
  socialMedia: {
    facebook?: string;
    twitter?: string;
    instagram?: string;
    linkedin?: string;
  };
  description: {
    tr: string;
    en: string;
    es: string;
  };
}

const Contact: React.FC = () => {
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo>({
    companyName: 'ZeepConstruction',
    address: {
      street: 'Atatürk Caddesi No: 123',
      city: 'İstanbul',
      state: 'İstanbul',
      zipCode: '34000',
      country: 'Türkiye'
    },
    contact: {
      phone: '+90 212 123 4567',
      fax: '+90 212 123 4568',
      email: 'info@zeepconstruction.com',
      website: 'https://www.zeepconstruction.com'
    },
    businessHours: {
      weekdays: '09:00 - 18:00',
      saturday: '09:00 - 14:00',
      sunday: 'Kapalı'
    },
    socialMedia: {
      facebook: 'https://facebook.com/zeepconstruction',
      twitter: 'https://twitter.com/zeepconstruction',
      instagram: 'https://instagram.com/zeepconstruction',
      linkedin: 'https://linkedin.com/company/zeepconstruction'
    },
    description: {
      tr: 'Su yönetimi ve boru sistemleri alanında 20 yılı aşkın deneyime sahip öncü şirket.',
      en: 'Leading company with over 20 years of experience in water management and pipe systems.',
      es: 'Empresa líder con más de 20 años de experiencia en gestión del agua y sistemas de tuberías.'
    }
  });

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<'tr' | 'en' | 'es'>('tr');

  // API'den şirket bilgilerini yükle (şu an için static data)
  const fetchCompanyInfo = async () => {
    try {
      // TODO: API endpoint oluşturulduğunda buraya eklenecek
      // const response = await api.get('/company-info');
      // setCompanyInfo(response.data);
      console.log('Company info loaded (using static data)');
    } catch (error) {
      console.error('Error fetching company info:', error);
      toast.error('Şirket bilgileri yüklenirken hata oluştu');
    }
  };

  useEffect(() => {
    fetchCompanyInfo();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // TODO: API endpoint oluşturulduğunda buraya eklenecek
      // await api.put('/company-info', companyInfo);
      
      toast.success('Şirket bilgileri başarıyla güncellendi');
      setEditing(false);
    } catch (error) {
      console.error('Error saving company info:', error);
      toast.error('Şirket bilgileri kaydedilirken hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setCompanyInfo(prev => {
      const keys = field.split('.');
      const newInfo = { ...prev };
      let current: any = newInfo;
      
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newInfo;
    });
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Şirket Bilgileri</h1>
              <p className="text-gray-600 mt-1">İletişim bilgileri ve şirket detaylarını yönetin</p>
            </div>
            <div className="flex space-x-3">
              {editing ? (
                <>
                  <button
                    onClick={() => setEditing(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
                  >
                    İptal
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 flex items-center space-x-2 disabled:opacity-50"
                  >
                    <Save className="h-4 w-4" />
                    <span>{saving ? 'Kaydediliyor...' : 'Kaydet'}</span>
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setEditing(true)}
                  className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 flex items-center space-x-2"
                >
                  <Edit className="h-4 w-4" />
                  <span>Düzenle</span>
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Company Basic Info */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Building className="h-5 w-5 mr-2" />
              Şirket Bilgileri
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Şirket Adı
                </label>
                {editing ? (
                  <input
                    type="text"
                    value={companyInfo.companyName}
                    onChange={(e) => handleInputChange('companyName', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900 py-2">{companyInfo.companyName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Açıklama
                </label>
                {editing && (
                  <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg mb-3">
                    {['tr', 'en', 'es'].map((lang) => (
                      <button
                        key={lang}
                        onClick={() => setSelectedLanguage(lang as 'tr' | 'en' | 'es')}
                        className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                          selectedLanguage === lang
                            ? 'bg-white text-primary-600 shadow-sm'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        {lang.toUpperCase()}
                      </button>
                    ))}
                  </div>
                )}
                {editing ? (
                  <textarea
                    rows={3}
                    value={companyInfo.description[selectedLanguage]}
                    onChange={(e) => handleInputChange(`description.${selectedLanguage}`, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900 py-2">{companyInfo.description.tr}</p>
                )}
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Phone className="h-5 w-5 mr-2" />
              İletişim Bilgileri
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Telefon
                </label>
                {editing ? (
                  <input
                    type="tel"
                    value={companyInfo.contact.phone}
                    onChange={(e) => handleInputChange('contact.phone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900 py-2">{companyInfo.contact.phone}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fax
                </label>
                {editing ? (
                  <input
                    type="tel"
                    value={companyInfo.contact.fax || ''}
                    onChange={(e) => handleInputChange('contact.fax', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900 py-2">{companyInfo.contact.fax || '-'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  E-posta
                </label>
                {editing ? (
                  <input
                    type="email"
                    value={companyInfo.contact.email}
                    onChange={(e) => handleInputChange('contact.email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900 py-2">{companyInfo.contact.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Website
                </label>
                {editing ? (
                  <input
                    type="url"
                    value={companyInfo.contact.website || ''}
                    onChange={(e) => handleInputChange('contact.website', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900 py-2">
                    {companyInfo.contact.website ? (
                      <a 
                        href={companyInfo.contact.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary-600 hover:text-primary-700"
                      >
                        {companyInfo.contact.website}
                      </a>
                    ) : '-'}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <MapPin className="h-5 w-5 mr-2" />
              Adres Bilgileri
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sokak Adresi
                </label>
                {editing ? (
                  <input
                    type="text"
                    value={companyInfo.address.street}
                    onChange={(e) => handleInputChange('address.street', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900 py-2">{companyInfo.address.street}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Şehir
                  </label>
                  {editing ? (
                    <input
                      type="text"
                      value={companyInfo.address.city}
                      onChange={(e) => handleInputChange('address.city', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  ) : (
                    <p className="text-gray-900 py-2">{companyInfo.address.city}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Posta Kodu
                  </label>
                  {editing ? (
                    <input
                      type="text"
                      value={companyInfo.address.zipCode}
                      onChange={(e) => handleInputChange('address.zipCode', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  ) : (
                    <p className="text-gray-900 py-2">{companyInfo.address.zipCode}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ülke
                </label>
                {editing ? (
                  <input
                    type="text"
                    value={companyInfo.address.country}
                    onChange={(e) => handleInputChange('address.country', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900 py-2">{companyInfo.address.country}</p>
                )}
              </div>
            </div>
          </div>

          {/* Business Hours & Social Media */}
          <div className="space-y-6">
            {/* Business Hours */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Clock className="h-5 w-5 mr-2" />
                Çalışma Saatleri
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Hafta İçi
                  </label>
                  {editing ? (
                    <input
                      type="text"
                      value={companyInfo.businessHours.weekdays}
                      onChange={(e) => handleInputChange('businessHours.weekdays', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  ) : (
                    <p className="text-gray-900 py-2">{companyInfo.businessHours.weekdays}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cumartesi
                  </label>
                  {editing ? (
                    <input
                      type="text"
                      value={companyInfo.businessHours.saturday || ''}
                      onChange={(e) => handleInputChange('businessHours.saturday', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  ) : (
                    <p className="text-gray-900 py-2">{companyInfo.businessHours.saturday || '-'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pazar
                  </label>
                  {editing ? (
                    <input
                      type="text"
                      value={companyInfo.businessHours.sunday || ''}
                      onChange={(e) => handleInputChange('businessHours.sunday', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  ) : (
                    <p className="text-gray-900 py-2">{companyInfo.businessHours.sunday || '-'}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Social Media */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Globe className="h-5 w-5 mr-2" />
                Sosyal Medya
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <Facebook className="h-4 w-4 mr-2" />
                    Facebook
                  </label>
                  {editing ? (
                    <input
                      type="url"
                      value={companyInfo.socialMedia.facebook || ''}
                      onChange={(e) => handleInputChange('socialMedia.facebook', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  ) : (
                    <p className="text-gray-900 py-2">
                      {companyInfo.socialMedia.facebook ? (
                        <a 
                          href={companyInfo.socialMedia.facebook} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:text-primary-700"
                        >
                          {companyInfo.socialMedia.facebook}
                        </a>
                      ) : '-'}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <Instagram className="h-4 w-4 mr-2" />
                    Instagram
                  </label>
                  {editing ? (
                    <input
                      type="url"
                      value={companyInfo.socialMedia.instagram || ''}
                      onChange={(e) => handleInputChange('socialMedia.instagram', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  ) : (
                    <p className="text-gray-900 py-2">
                      {companyInfo.socialMedia.instagram ? (
                        <a 
                          href={companyInfo.socialMedia.instagram} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:text-primary-700"
                        >
                          {companyInfo.socialMedia.instagram}
                        </a>
                      ) : '-'}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <Linkedin className="h-4 w-4 mr-2" />
                    LinkedIn
                  </label>
                  {editing ? (
                    <input
                      type="url"
                      value={companyInfo.socialMedia.linkedin || ''}
                      onChange={(e) => handleInputChange('socialMedia.linkedin', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  ) : (
                    <p className="text-gray-900 py-2">
                      {companyInfo.socialMedia.linkedin ? (
                        <a 
                          href={companyInfo.socialMedia.linkedin} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:text-primary-700"
                        >
                          {companyInfo.socialMedia.linkedin}
                        </a>
                      ) : '-'}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default Contact;
